import offerCardSmall from './OfferCardSmall.html'
import offerCardFull from './OfferCard.html'
import { getProcessTemplateCode } from './offerCardUtils'
import { generateColorPaletteWithoutBabel, getContrastColor } from './colorUtils'

const convertModelToEmailFormat = (model) => {
  if (!model) return model
  if (model === 'category_repurchase') return 'repurchase'
  if (model === 'category_rescue') return 'recovery'
  if (model === 'category_activation') return 'activation'
  return model.replace(/^category_/, '')
}

export function extractBestOfferOrder(builderEmailConfiguration) {
  if (!builderEmailConfiguration || !builderEmailConfiguration.body || !builderEmailConfiguration.body.rows) {
    return []
  }

  const bestOfferOrder = []

  builderEmailConfiguration.body.rows.forEach((row, rowIndex) => {
    if (row.columns) {
      row.columns.forEach((column, columnIndex) => {
        if (column.contents && column.contents.length > 0) {
          column.contents.forEach((content, contentIndex) => {
            if (content && content.type === 'custom' && content.slug === 'offer_card' && content.values && content.values.bestOffer && content.values.model) {
              const model = content.values.model
              const emailModel = convertModelToEmailFormat(model)
              const orderItem = {
                rowIndex,
                columnIndex,
                contentIndex,
                bestOffer: content.values.bestOffer,
                model: emailModel,
                htmlID: content.values._meta?.htmlID,
              }
              bestOfferOrder.push(orderItem)
            }
          })
        }
      })
    }
  })
  return bestOfferOrder
}

export function isEditMode(message) {
  if (!message) return false

  const processedHtml = message.processed_html
  if (typeof processedHtml === 'string' && processedHtml.trim().length > 0) {
    return true
  }

  const cfgRaw =
    message.builder_email_configuration || message.builderEmailConfiguration || message.builder_configuration || message.design || message.configuration

  let cfg = cfgRaw
  if (typeof cfgRaw === 'string') {
    try {
      cfg = JSON.parse(cfgRaw)
    } catch (e) {
      cfg = null
    }
  }

  const hasRows = !!(cfg && cfg.body && Array.isArray(cfg.body.rows) && cfg.body.rows.length > 0)
  const hasCounters = !!(cfg && cfg.counters && (cfg.counters.u_content_custom_offer_card > 0 || cfg.counters.u_content_custom_offer > 0))

  return hasRows || hasCounters
}

export function getAutoDistributedOfferCardCustomJS(
  defaultColors,
  maxOfferCards = 10,
  message = null,
  modelOptions = [],
  defaultModelValue = 'category_activation',
  modelQuantityMap = {}
) {
  const emailDefaultModel = convertModelToEmailFormat(defaultModelValue)

  const emailModelOptions = modelOptions.map((opt) => ({
    ...opt,
    value: convertModelToEmailFormat(opt.value),
  }))

  const emailModelQuantityMap = {}
  Object.keys(modelQuantityMap).forEach((key) => {
    const emailKey = convertModelToEmailFormat(key)
    emailModelQuantityMap[emailKey] = modelQuantityMap[key]
  })
  const processTemplateCode = getProcessTemplateCode({
    includeModel: true,
    defaultModelValue: defaultModelValue,
  })
  const generateColorPaletteCode = generateColorPaletteWithoutBabel.toString()
  const getContrastColorCode = getContrastColor.toString()
  const baseColor = defaultColors?.finish?.main || '#011F3E'
  const colorPalette = generateColorPaletteWithoutBabel(baseColor)

  const isEdit = isEditMode(message)
  const bestOfferOrder = isEdit ? extractBestOfferOrder(message?.builder_email_configuration) : []

  const modelOptionsJson = JSON.stringify(
    emailModelOptions.length > 0
      ? emailModelOptions
      : [
          { label: 'Ativação', value: 'activation' },
          { label: 'Recompra', value: 'repurchase' },
          { label: 'Resgate', value: 'recovery' },
        ]
  )

  const defaultModelLabel = emailModelOptions.length > 0 ? emailModelOptions.find((m) => m.value === emailDefaultModel)?.label || 'Ativação' : 'Ativação'

  const modelQuantityMapJson = JSON.stringify(emailModelQuantityMap || {})

  const maxQuantity = Math.max(...Object.values(modelQuantityMap), Math.min(maxOfferCards, 10))

  const initialBestOfferOptions = []
  for (let i = 1; i <= maxQuantity; i++) {
    initialBestOfferOptions.push({ label: `${i}ª Melhor Oferta`, value: i.toString() })
  }

  return `
    (function() {
      const htmlTemplateSmall = \`${offerCardSmall}\`;
      const htmlTemplateLarge = \`${offerCardFull}\`;
      
      const processTemplate = ${processTemplateCode};
      const generateColorPaletteWithoutBabel  = ${generateColorPaletteCode};
      const getContrastColor = ${getContrastColorCode};
      
      const modelOptions = ${modelOptionsJson};
      const modelQuantityMap = ${modelQuantityMapJson};
      const defaultModel = '${emailDefaultModel}';
      
      let currentCardModel = defaultModel;
      const cardModelMap = new Map();
      const lastUpdatedModel = new Map();
      let postMessageTimeout = null;

      if (typeof window.__offerCardTemplateLoadUntil === 'undefined') {
        window.__offerCardTemplateLoadUntil = 0;
      }
      if (!window.__offerCardTemplateLoadListenerInstalled) {
        window.__offerCardTemplateLoadListenerInstalled = true;
        window.addEventListener('message', (event) => {
          if (!event || !event.data) return;
          if (event.data.type === 'offerCardTemplateLoad' && event.data.source === 'parent-window') {
            const active = !!event.data.active;
            const ttlMs = typeof event.data.ttlMs === 'number' ? event.data.ttlMs : 5000;
            window.__offerCardTemplateLoadUntil = active ? (Date.now() + Math.max(0, ttlMs)) : 0;
          }
        }); 
      }
      window.__setOfferCardTemplateLoad = (active, ttlMs = 5000) => {
        window.__offerCardTemplateLoadUntil = active ? (Date.now() + Math.max(0, ttlMs)) : 0;
      };
      let lastSentModel = null;
      
      unlayer.registerTool({
        name: 'offer_card',
        label: 'Oferta',
        icon: 'fa-tag',
        supportedDisplayModes: ['web', 'email'],
        options: {
          model: {
            title: 'Critério de seleção de oferta',
            position: 0,
            collapsed: false,
            options: {
              model: {
                label: 'Critério de seleção de oferta',
                defaultValue: defaultModel,
                widget: 'dropdown',
                options: modelOptions,
              },
            },
          },
          bestOffer: {
            title: 'Melhor Oferta',
            position: 1,
            collapsed: false,
            options: {
              bestOffer: {
                label: 'Melhor Oferta',
                defaultValue: '1',
                widget: 'dropdown',
                options: (values) => {
                  const model = values?.model || defaultModel;
                  const modelNameMap = {
                    'repurchase': 'Recompra',
                    'recovery': 'Resgate',
                    'activation': 'Ativação'
                  };
                  const modelName = modelNameMap[model] || 'Ativação';
                  const quantity = modelQuantityMap[model] || ${Math.min(maxOfferCards, 10)};
                  const maxQuantity = Math.min(quantity, ${maxOfferCards});
                  
                  const options = [];
                  for (let i = 1; i <= maxQuantity; i++) {
                    options.push({ 
                      label: i + 'ª Melhor Oferta', 
                      value: i.toString() 
                    });
                  }
                  return options;
                },
              },
            },
          },
          size: {
            title: 'Tamanho',
            position: 1,
            collapsed: false,
            options: {
              templateSize: {
                label: 'Tamanho do Cartão',
                defaultValue: 'small',
                widget: 'dropdown'
              }
            }
          },
          font: {
            title: 'Fontes',
            position: 1,
            collapsed: false,
            options: {
              fontFamily: {
                label: 'Fonte',
                defaultValue: {
                  "label": "Lato",
                  "value": "'Lato',sans-serif"
                },
                widget: 'font_family',
              }
            }
          },
          colors: {
            title: 'Cores',
            position: 1,
            collapsed: false,
            options: {
              mecanicaOfferColor: {
                label: 'Cor principal (Mecânica)',
                defaultValue: '${baseColor}',
                widget: 'color_picker',
              },
              descontoColor: {
                label: 'Cor secundária (Desconto)',
                defaultValue: '${colorPalette.light}',
                widget: 'color_picker',
              },
            },
          },
        },
        transformer: (values, source) => {
          const modelNameMap = {
            'repurchase': 'Recompra',
            'recovery': 'Resgate',
            'activation': 'Ativação'
          };
          
          const defaultModel = '${emailDefaultModel}';

          if (source.name === 'mecanicaOfferColor') {
            const colorPalette = generateColorPaletteWithoutBabel(source.value);

            const next = {
              ...values,
              mecanicaOfferColor: source.value,
              descontoColor: colorPalette.light,
            };

            try {
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('offerCardColorChange', {
                  detail: { mecanicaColor: source.value, descontoColor: colorPalette.light },
                }));
              }
            } catch {}

            return next;
          }

          if (source.name === 'descontoColor') {
            const next = { ...values, descontoColor: source.value };

            try {
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('offerCardColorChange', {
                  detail: { mecanicaColor: null, descontoColor: source.value },
                }));
              }
            } catch {}

            return next;
          }

          if (source.name === 'fontFamily') {
            const font = (source.value && typeof source.value === 'object')
              ? source.value
              : { label: 'Lato', value: "'Lato',sans-serif" };

            const next = { ...values, fontFamily: font };

            try {
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('offerCardFontChange', {
                  detail: { fontFamily: font },
                }));
              }
            } catch {}

            return next;
          }

          if (source.name === 'model') {
            let model = source.value;
            if (!model && values.model) {
              model = values.model;
            }
            if (!model) {
              model = defaultModel;
            }
            const modelName = modelNameMap[model] || 'Ativação';
            
            currentCardModel = model;
            if (values._meta?.htmlID) {
              cardModelMap.set(values._meta.htmlID, model);
              if (typeof window !== 'undefined') {
                if (!window.cardModelMap) {
                  window.cardModelMap = new Map();
                }
                window.cardModelMap.set(values._meta.htmlID, model);
              }
            }
            
            const quantity = modelQuantityMap[model] || ${Math.min(maxOfferCards, 10)};
            const maxOffers = Math.min(quantity, 10);
            
            const lastModel = lastUpdatedModel.get(values._meta?.htmlID);
            
            
            let offerNumber = '1';
            if (lastModel !== model) {
              const htmlID = values._meta?.htmlID;
              const normalizedModel = model.replace(/^category_/, '');
              const normalizedModelForAria = normalizedModel === 'rescue' ? 'recovery' : normalizedModel;
              
              const allCardsByAria = document.body.querySelectorAll('[aria-offer]');
              const allCardsByClass = document.body.querySelectorAll('.u_content_custom_offer_card');
              const currentCardElement = htmlID ? document.getElementById(htmlID) : null;
              const existingNumbers = [];
              
              const extractOfferNumberFromText = (text) => {
                if (!text) return null;
                const regex = new RegExp('(\\\\d+)ª\\\\s*Melhor\\\\s*Oferta');
                const match = text.match(regex);
                if (match && match[1]) {
                  return parseInt(match[1], 10);
                }
                return null;
              };
              
              // Função auxiliar para obter o modelo atual de um card
              const getCardCurrentModel = (card, cardHtmlID) => {
                // 1. Verificar no window.cardModelMap (mais confiável)
                if (cardHtmlID && window.cardModelMap && window.cardModelMap.has && window.cardModelMap.has(cardHtmlID)) {
                  const modelFromMap = window.cardModelMap.get(cardHtmlID);
                  return modelFromMap;
                }
                
                // 2. Verificar no texto visível (segunda opção mais confiável)
                const nomeOfertaElement = card.querySelector('.product-name, .offer-card-product-name');
                if (nomeOfertaElement) {
                  const text = nomeOfertaElement.textContent || nomeOfertaElement.innerText || '';
                  if (text.includes('–')) {
                    const parts = text.split('–');
                    if (parts.length > 1) {
                      const modelNameFromText = parts[1].trim();
                      // Mapear nome do modelo de volta para o valor
                      const modelValue = Object.keys(modelNameMap).find(key => modelNameMap[key] === modelNameFromText);
                      if (modelValue) {
                        return modelValue;
                      }
                    }
                  }
                }
                
                // 3. Se não encontrou, retornar null (não sabemos o modelo)
                return null;
              };
              
              // Processar cards com aria-offer
              const modelPattern = 'best-offer-' + normalizedModelForAria + '-';
              Array.from(allCardsByAria).forEach(card => {
                const ariaOffer = card.getAttribute('aria-offer');
                if (!ariaOffer) return;
                
                // Verificar se é o card atual (incluindo filhos)
                const isCurrentCard = currentCardElement && (card === currentCardElement || currentCardElement.contains(card));
                if (isCurrentCard) {
                  return;
                }
                
                // Obter o modelo atual do card
                const cardHtmlID = card.id || card.getAttribute('data-html-id');
                const cardCurrentModel = getCardCurrentModel(card, cardHtmlID);
                const cardNormalizedModel = cardCurrentModel ? cardCurrentModel.replace(/^category_/, '') : null;
                const cardNormalizedModelForAria = cardNormalizedModel === 'rescue' ? 'recovery' : cardNormalizedModel;
                
                // Verificar se o modelo atual do card corresponde ao modelo que estamos procurando
                const modelMatches = cardNormalizedModelForAria === normalizedModelForAria;
                
                // Só contar se o modelo atual corresponder E o aria-offer também corresponder (ou não tiver modelo atual)
                if (modelMatches && ariaOffer.startsWith(modelPattern)) {
                  const number = parseInt(ariaOffer.replace(modelPattern, ''), 10);
                  if (!isNaN(number)) {
                    existingNumbers.push(number);
                  }
                } else if (!cardCurrentModel && ariaOffer.startsWith(modelPattern)) {
                  // Se não sabemos o modelo atual mas o aria-offer corresponde, contar (fallback)
                  const number = parseInt(ariaOffer.replace(modelPattern, ''), 10);
                  if (!isNaN(number)) {
                    existingNumbers.push(number);
                  }
                }
              });
              
              // Processar cards pelo elemento customizado (verificar texto visível)
              Array.from(allCardsByClass).forEach(card => {
                // Verificar se é o card atual (incluindo filhos e por ID)
                const cardHtmlID = card.id || card.getAttribute('data-html-id');
                const isCurrentCard = currentCardElement && (
                  card === currentCardElement || 
                  currentCardElement.contains(card) || 
                  cardHtmlID === htmlID
                );
                if (isCurrentCard) {
                  return;
                }
                
                // Obter o modelo atual do card
                const cardCurrentModel = getCardCurrentModel(card, cardHtmlID);
                const cardNormalizedModel = cardCurrentModel ? cardCurrentModel.replace(/^category_/, '') : null;
                const cardNormalizedModelForAria = cardNormalizedModel === 'rescue' ? 'recovery' : cardNormalizedModel;
                
                // Verificar se o modelo atual do card corresponde ao modelo que estamos procurando
                const modelMatches = cardNormalizedModelForAria === normalizedModelForAria;
                
                // Primeiro verificar aria-offer (mais confiável)
                const ariaOffer = card.getAttribute('aria-offer');
                if (ariaOffer) {
                  if (ariaOffer.startsWith(modelPattern) && modelMatches) {
                    const number = parseInt(ariaOffer.replace(modelPattern, ''), 10);
                    if (!isNaN(number) && !existingNumbers.includes(number)) {
                      existingNumbers.push(number);
                    }
                    return; // Já encontrou pelo aria-offer, não precisa verificar texto
                  } else if (!modelMatches) {
                    return;
                  } else {
                    return;
                  }
                }
                
                // Se não tem aria-offer, verificar texto visível
                const nomeOfertaElement = card.querySelector('.product-name, .offer-card-product-name');
                if (nomeOfertaElement) {
                  const text = nomeOfertaElement.textContent || nomeOfertaElement.innerText || '';
                  // Verificar se o texto termina com " – " + nome do modelo
                  // Isso garante que estamos contando apenas cards do modelo correto
                  const expectedSuffix = ' – ' + modelName;
                  if (text.endsWith(expectedSuffix) && modelMatches) {
                    const number = extractOfferNumberFromText(text);
                    if (number && !isNaN(number) && !existingNumbers.includes(number)) {
                      existingNumbers.push(number);
                    }
                  }
                }
              });
              
              // Ordenar números existentes
              existingNumbers.sort((a, b) => a - b);
              
              // Encontrar próximo número disponível
              let nextNumber = 1;
              while (existingNumbers.includes(nextNumber) && nextNumber <= maxOffers) {
                nextNumber++;
              }
              
              // Se atingiu o limite máximo, usar o máximo disponível
              if (nextNumber > maxOffers) {
                nextNumber = Math.max(1, maxOffers);
              }
              
              // Garantir que sempre seja pelo menos 1
              if (nextNumber < 1 || isNaN(nextNumber)) {
                nextNumber = 1;
              }
              
              offerNumber = nextNumber.toString();
            } else {
              // Modelo não mudou, manter o número atual se válido
              offerNumber = values.bestOffer || '1';
              const currentOfferNum = parseInt(offerNumber, 10);
              if (isNaN(currentOfferNum) || currentOfferNum < 1 || currentOfferNum > maxOffers) {
                offerNumber = '1';
              }
            }
            
            const offerName = offerNumber + 'ª Melhor Oferta – ' + modelName;
            
            if (lastModel === model) {
              const returnValues = { ...values, model: model, bestOffer: offerNumber, 'nome da oferta': offerName };
              return returnValues;
            }
            
            if (values._meta?.htmlID) {
              lastUpdatedModel.set(values._meta.htmlID, model);
            }
            
            // Atualizar window.__lastSelectedModel quando o modelo mudar
            if (typeof window !== 'undefined') {
              window.__lastSelectedModel = model;
            }

            if (window.parent && window.parent !== window) {
              if (postMessageTimeout) {
                clearTimeout(postMessageTimeout);
              }
              
              if (lastSentModel !== model) {
                postMessageTimeout = setTimeout(() => {
                  window.parent.postMessage({
                    type: 'offerCardModelChange',
                    source: 'unlayer-iframe',
                    detail: {
                      model: model,
                      modelName: modelName,
                      htmlID: values._meta?.htmlID,
                      quantity: quantity
                    }
                  }, '*');
                  lastSentModel = model;
                  postMessageTimeout = null;
                }, 200);
              }
            }
            
            const returnValues = { ...values, model: model, bestOffer: offerNumber, 'nome da oferta': offerName };
            
            // Armazenar o último modelo para atualizar quando o painel aparecer
            let lastModelForDropdownUpdate = null;
            
            // Função para atualizar o dropdown de melhor oferta baseado no modelo
            const updateBestOfferDropdownForModel = (targetModel, retryCount = 0) => {
              
              // Armazenar o modelo para atualização posterior
              lastModelForDropdownUpdate = targetModel;
              
              const maxQuantityForModel = modelQuantityMap[targetModel] || ${Math.min(maxOfferCards, 10)};
              // Garantir que maxOffers use o valor correto - se maxQuantityForModel > 0, usar ele (limitado por 10)
              // Se maxQuantityForModel for 0 ou undefined, usar o fallback
              const maxOffers = maxQuantityForModel > 0 ? Math.min(maxQuantityForModel, 10) : ${Math.min(maxOfferCards, 10)};
              
              // Função auxiliar para realmente atualizar o dropdown
              // Usa a mesma abordagem de quando adiciona um novo card: procura os itens diretamente no documento
              const doUpdate = () => {
                // Primeiro, tentar encontrar os itens diretamente no documento (mesma abordagem usada quando adiciona card)
                // Isso funciona porque quando o dropdown está aberto, os itens aparecem no DOM
                let dropdownItems = document.querySelectorAll('.dropdown-item, [role="option"], .blockbuilder-dropdown-item');
                
                // Filtrar apenas os itens que são relacionados a "Melhor Oferta"
                if (dropdownItems.length > 0) {
                  const bestOfferItems = Array.from(dropdownItems).filter(item => {
                    const label = item.querySelector('.label, .blockbuilder-label');
                    if (!label) return false;
                    const text = label.textContent || label.innerText || '';
                    return text.includes('Melhor Oferta') || text.includes('ª');
                  });
                  
                  if (bestOfferItems.length > 0) {
                    dropdownItems = bestOfferItems;
                  }
                }
                
                // Se não encontrou itens diretamente, tentar encontrar o painel e depois o menu
                if (dropdownItems.length === 0) {
                  
                  let bestOfferPanel = document.querySelector('.bestOffer_panel') ||
                                       document.querySelector('[data-property="bestOffer"]') ||
                                       document.querySelector('.property-panel [data-property="bestOffer"]') ||
                                       document.querySelector('.unlayer-property-panel [data-property="bestOffer"]');
                  
                  // Se ainda não encontrou, procurar por texto "Melhor Oferta"
                  if (!bestOfferPanel) {
                    const allPanels = document.querySelectorAll('.property-panel, [data-property], .unlayer-property-panel');
                    for (let panel of allPanels) {
                      const text = panel.textContent || panel.innerText || '';
                      if (text.includes('Melhor Oferta') || text.includes('bestOffer') || panel.getAttribute('data-property') === 'bestOffer') {
                        bestOfferPanel = panel;
                        break;
                      }
                    }
                  }
                  
                  if (bestOfferPanel) {
                    let dropdownMenu = bestOfferPanel.querySelector('.dropdown-menu, [role="listbox"], .unlayer-dropdown-menu, .blockbuilder-dropdown-menu');
                    
                    // Se não encontrou no painel, procurar no documento (dropdown pode estar aberto)
                    if (!dropdownMenu) {
                      dropdownMenu = document.querySelector('.dropdown-menu.show, .dropdown-menu[style*="display"], [role="listbox"]:not([hidden])');
                    }
                    
                    if (dropdownMenu) {
                      dropdownItems = dropdownMenu.querySelectorAll('.dropdown-item, [role="option"], .blockbuilder-dropdown-item');
                    }
                  }
                }
                
                if (dropdownItems.length === 0) {
                  return false;
                }
                
                let hiddenCount = 0;
                let visibleCount = 0;
                
                dropdownItems.forEach((item) => {
                  const label = item.querySelector('.label, .blockbuilder-label');
                  if (!label) return;
                  
                  const itemText = label.textContent || label.innerText || '';
                  // Extrair o número da oferta do texto (ex: "4ª Melhor Oferta" -> 4)
                  const numberMatch = itemText.match(/(\\d+)/);
                  if (!numberMatch) return;
                  
                  const itemNumber = parseInt(numberMatch[1], 10);
                  
                  // Esconder opções que excedem a quantidade máxima do modelo
                  if (itemNumber > maxOffers) {
                    item.style.display = 'none';
                    item.style.visibility = 'hidden';
                    item.setAttribute('aria-hidden', 'true');
                    hiddenCount++;
                  } else {
                    item.style.display = '';
                    item.style.visibility = '';
                    item.removeAttribute('aria-hidden');
                    visibleCount++;
                  }
                });
                
                return true;
              };
              
              // Tentar atualizar imediatamente
              if (!doUpdate()) {
                // Se não conseguiu, fazer retry
                if (retryCount < 10) {
                  setTimeout(() => {
                    updateBestOfferDropdownForModel(targetModel, retryCount + 1);
                  }, 200 * (retryCount + 1));
                }
              }
            };
            
            // MutationObserver para detectar quando o painel aparece
            const setupBestOfferPanelObserver = () => {
              const panelObserver = new MutationObserver((mutations) => {
                if (lastModelForDropdownUpdate) {
                  mutations.forEach((mutation) => {
                    if (mutation.type === 'childList') {
                      const addedNodes = Array.from(mutation.addedNodes);
                      const hasBestOfferPanel = addedNodes.some(node => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                          return node.classList?.contains('bestOffer_panel') ||
                                 node.getAttribute?.('data-property') === 'bestOffer' ||
                                 node.querySelector?.('.bestOffer_panel, [data-property="bestOffer"]');
                        }
                        return false;
                      });
                      
                      if (hasBestOfferPanel) {
                        setTimeout(() => {
                          updateBestOfferDropdownForModel(lastModelForDropdownUpdate, 0);
                        }, 100);
                      }
                    }
                  });
                }
              });
              
              panelObserver.observe(document.body, {
                childList: true,
                subtree: true
              });
              
              // Também observar quando dropdown é aberto
              const dropdownObserver = new MutationObserver((mutations) => {
                if (lastModelForDropdownUpdate) {
                  mutations.forEach((mutation) => {
                    if (mutation.type === 'childList') {
                      const addedNodes = Array.from(mutation.addedNodes);
                      const hasDropdownMenu = addedNodes.some(node => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                          return node.classList?.contains('dropdown-menu') ||
                                 node.getAttribute?.('role') === 'listbox' ||
                                 node.querySelector?.('.dropdown-menu, [role="listbox"]');
                        }
                        return false;
                      });
                      
                      if (hasDropdownMenu) {
                        setTimeout(() => {
                          updateBestOfferDropdownForModel(lastModelForDropdownUpdate, 0);
                        }, 50);
                      }
                    }
                  });
                }
              });
              
              dropdownObserver.observe(document.body, {
                childList: true,
                subtree: true
              });
            };
            
            setupBestOfferPanelObserver();
            
            // Expor função globalmente para poder ser chamada quando o card é adicionado
            window.updateBestOfferDropdownForModel = updateBestOfferDropdownForModel;
            
            setTimeout(() => {
              // Atualizar dropdown baseado no modelo atual
              updateBestOfferDropdownForModel(model);
              
              if (values._meta?.htmlID) {
                const cardElement = document.getElementById(values._meta.htmlID);
                if (cardElement) {
                  const nomeOfertaElements = cardElement.querySelectorAll('.product-name, .offer-card-product-name');
                  
                  nomeOfertaElements.forEach((element) => {
                    const currentText = element.textContent || element.innerText || '';
                    const normalizedText = currentText.replace(new RegExp('\\s+', 'g'), ' ').trim();
                    const normalizedPlaceholder = '{{nome da oferta}}';
                    const hasPlaceholder = normalizedText.includes(normalizedPlaceholder) || 
                                         normalizedText.includes('{{nomedaoferta}}') ||
                                         (normalizedText.includes('{{nome') && normalizedText.includes('oferta}}'));
                    
                    if (hasPlaceholder || normalizedText === '' || normalizedText === normalizedPlaceholder || normalizedText !== offerName.trim()) {
                      element.textContent = offerName;
                      element.innerText = offerName;
                      
                      element.dispatchEvent(new Event('input', { bubbles: true }));
                      element.dispatchEvent(new Event('change', { bubbles: true }));
                      
                      setTimeout(() => {
                        const updatedText = element.textContent || element.innerText || '';
                      }, 50);
                    }
                  });
                  
                  if (window.unlayer && window.unlayer.getEditor) {
                    try {
                      const editor = window.unlayer.getEditor();
                      if (editor) {
                        if (editor.updateSelectedElement) {
                          editor.updateSelectedElement({
                            'nome da oferta': offerName,
                            model: model,
                            bestOffer: offerNumber
                          });
                        }
                        if (editor.dispatchEvent) {
                          editor.dispatchEvent('design:updated');
                        }
                      }
                    } catch (e) {
                    }
                  }
                }
              }
            }, 100);
            
            // Aguardar um pouco mais para garantir que o dropdown foi renderizado
            setTimeout(() => {
              updateBestOfferDropdownForModel(model);
            }, 300);
            
            return returnValues;
          }

          if (source.name === 'bestOffer') {
            
            let offerNumber = source.value;
            let model = values.model || defaultModel;
            if ((!values.model || values.model === defaultModel) && typeof window !== 'undefined' && window.__lastSelectedModel) {
              model = window.__lastSelectedModel;
            }
            const modelName = modelNameMap[model] || 'Ativação';
            
            // Validar que o número da oferta não exceda o limite disponível para este modelo
            const quantity = modelQuantityMap[model] || ${Math.min(maxOfferCards, 10)};
            // Usar o quantity do modelo específico, limitado apenas por 10 (máximo de opções no dropdown)
            // Não limitar por maxOfferCards aqui, pois cada modelo pode ter sua própria quantidade
            const maxOffers = Math.max(1, Math.min(quantity, 10)); // Garantir que seja pelo menos 1 e no máximo 10
            const currentOfferNum = parseInt(offerNumber, 10);
            
            
            // Garantir que seja um número válido e pelo menos 1
            if (isNaN(currentOfferNum) || currentOfferNum < 1) {
              offerNumber = '1';
            } else if (currentOfferNum > maxOffers) {
              offerNumber = maxOffers.toString();
            } else {
              offerNumber = currentOfferNum.toString();
            }
            
            const offerName = offerNumber + 'ª Melhor Oferta – ' + modelName;
            
            
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                if (values._meta?.htmlID) {
                  const cardElement = document.getElementById(values._meta.htmlID);
                  
                  if (cardElement) {
                    const nomeOfertaElements = cardElement.querySelectorAll('.product-name, .offer-card-product-name');
                    
                    nomeOfertaElements.forEach((element) => {
                      const currentText = element.textContent || element.innerText || '';
                      const normalizedText = currentText.replace(new RegExp('\\s+', 'g'), ' ').trim();
                      const normalizedPlaceholder = '{{nome da oferta}}';
                      const hasPlaceholder = normalizedText.includes(normalizedPlaceholder) || 
                                           normalizedText.includes('{{nomedaoferta}}') ||
                                           (normalizedText.includes('{{nome') && normalizedText.includes('oferta}}'));
                      
                      if (hasPlaceholder || normalizedText === '' || normalizedText === normalizedPlaceholder || normalizedText !== offerName.trim()) {
                        element.textContent = offerName;
                        element.innerText = offerName;
                      }
                    });
                  }
                }
              });
            });
            
            const newValues = { ...values, 'nome da oferta': offerName, model: model };
            
            return newValues;
          }

          return values;
        },
        values: {
          'nome da oferta': '1ª Melhor Oferta – ${defaultModelLabel}',
          'unidade': 'Unidade',
          'fim da validade': 'Válido até [data validade]',
          'desconto da oferta': 'Desconto',
          'imagem da oferta': 'Imagem do produto ofertado',
          'regra min max': 'Regra min/máx',
          'mecanica oferta': 'Mecânica oferta',
          templateSize: 'small',
          fontFamily: {
            "label": "Lato",
            "value": "'Lato',sans-serif"
          },
          bestOffer: '1',
          model: defaultModel,
          mecanicaOfferColor: '${baseColor}',
          descontoColor: '${colorPalette.light}'
        },
        renderer: {
          Viewer: unlayer.createViewer({
            render: function(values, data) {
              const selectedSize = values.templateSize || 'small';
              const selectedTemplate = selectedSize === 'large' ? htmlTemplateLarge : htmlTemplateSmall;
              const rendered = processTemplate(selectedTemplate, values);
              return rendered;
            },
          }),
          exporters: {
            web: function (values, data) {
              const selectedSize = values.templateSize || 'small';
              const selectedTemplate = selectedSize === 'large' ? htmlTemplateLarge : htmlTemplateSmall;
              const rendered = processTemplate(selectedTemplate, values);
              return rendered;
            },
            email: function (values, data) {
              const selectedSize = values.templateSize || 'small';
              const selectedTemplate = selectedSize === 'large' ? htmlTemplateLarge : htmlTemplateSmall;
              const rendered = processTemplate(selectedTemplate, values);
              return rendered;
            }
          },
          head: {
            css: function (values, data) {
              if (!values || !values._meta) {
                return '';
              }
              
              const htmlID = values._meta.htmlID;
              const fontFamily = values.fontFamily && values.fontFamily.value ? values.fontFamily.value : (values.fontFamily || 'Lato');
              
              const mecanicaColor = values.mecanicaOfferColor || '${baseColor}';
              
              let cssString = \`
                #\${values._meta.htmlID} .offer-card-small-container table,td { 
                  color: \${getContrastColor(values.descontoColor)};
                } 

                #\${values._meta.htmlID} .offer-card-small-container {
                  font-family: \${fontFamily} !important;
                }
                
                #\${values._meta.htmlID} .offer-card-small-badge {
                  background: \${mecanicaColor} !important;
                  color: \${getContrastColor(mecanicaColor)} !important;
                }
                
                #\${values._meta.htmlID} .discount-offer-container-small {
                  background: \${values.descontoColor} !important;
                  color: \${getContrastColor(values.descontoColor)} !important;
                }

                @media (prefers-color-scheme: dark) {
                  #\${values._meta.htmlID} .offer-card-small-container table{
                    background-color: #FFFFFF !important;
                  }

                  #\${values._meta.htmlID} .offer-card-small-container table,td { 
                    color: \${getContrastColor(values.descontoColor)};
                  } 

                  #\${values._meta.htmlID} .offer-card-small-badge {
                    background: \${mecanicaColor} !important;
                    color: \${getContrastColor(mecanicaColor)} !important;
                  }
                  
                  #\${values._meta.htmlID} .discount-offer-container-small {
                    background: \${values.descontoColor} !important;
                    color: \${getContrastColor(values.descontoColor)} !important;
                  }
                  
                  #\${values._meta.htmlID}[data-ogsc] .offer-card-small-container,
                  #\${values._meta.htmlID}[data-ogsc] .offer-card-small-container * {
                    background-color: #FFFFFF !important;
                    color: inherit !important;
                  }
                  #\${values._meta.htmlID}[data-darkreader-mode] .offer-card-small-container,
                  #\${values._meta.htmlID}[data-darkreader-mode] .offer-card-small-container * {
                    background-color: #FFFFFF !important;
                    color: inherit !important;
                  }
                }

                #\${values._meta.htmlID} .offer-card-container {
                  font-family: \${fontFamily} !important;
                }
                
                #\${values._meta.htmlID} .offer-card-badge {
                  background-color: \${mecanicaColor} !important;
                  color: \${getContrastColor(mecanicaColor)} !important;
                  font-family: \${fontFamily} !important;
                }
                
                #\${values._meta.htmlID} .offer-card-image-text {
                  font-family: \${fontFamily} !important;
                }
                
                #\${values._meta.htmlID} .offer-card-rule {
                  font-family: \${fontFamily} !important;
                }
                
                #\${values._meta.htmlID} .offer-card-footer {
                  padding-left: 12px;
                  vertical-align: bottom;
                }
                
                #\${values._meta.htmlID} .offer-card-product-name {
                  font-family: \${fontFamily} !important;
                }
                
                #\${values._meta.htmlID} .offer-card-unit {
                  font-family: \${fontFamily} !important;
                }
                
                #\${values._meta.htmlID} .offer-card-footer-item {
                  font-family: \${fontFamily} !important;
                }
                
                #\${values._meta.htmlID} .discount-offer-container {
                  background-color: \${values.descontoColor} !important;
                  font-family: \${fontFamily} !important;
                  color: \${getContrastColor(values.descontoColor)} !important;
                }

                @media (prefers-color-scheme: dark) {
                  #\${values._meta.htmlID} .offer-card-container table{
                    background-color: #FFFFFF !important;
                  }

                 #\${values._meta.htmlID} .offer-card-badge {
                    background-color: \${mecanicaColor} !important;
                    color: \${getContrastColor(mecanicaColor)} !important;
                    font-family: \${fontFamily} !important;
                  }
                  
                  #\${values._meta.htmlID} .offer-card-image-text {
                    font-family: \${fontFamily} !important;
                  }
                  
                  #\${values._meta.htmlID} .offer-card-rule {
                    font-family: \${fontFamily} !important;
                  }
                  
                  #\${values._meta.htmlID} .offer-card-footer {
                    padding-left: 12px;
                    vertical-align: bottom;
                  }
                  
                  #\${values._meta.htmlID} .offer-card-product-name {
                    font-family: \${fontFamily} !important;
                  }
                  
                  #\${values._meta.htmlID} .offer-card-unit {
                    font-family: \${fontFamily} !important;
                  }
                  
                  #\${values._meta.htmlID} .offer-card-footer-item {
                    font-family: \${fontFamily} !important;
                  }
                  
                  #\${values._meta.htmlID} .discount-offer-container {
                    background-color: \${values.descontoColor} !important;
                    font-family: \${fontFamily} !important;
                    color: \${getContrastColor(values.descontoColor)} !important;
                  }

                  #\${values._meta.htmlID}[data-ogsc] .offer-card-container,
                  #\${values._meta.htmlID}[data-ogsc] .offer-card-container * {
                    background-color: #FFFFFF !important;
                    color: inherit !important;
                  }
                  #\${values._meta.htmlID}[data-darkreader-mode] .offer-card-container,
                  #\${values._meta.htmlID}[data-darkreader-mode] .offer-card-container * {
                    background-color: #FFFFFF !important;
                    color: inherit !important;
                  }
                }

                @media only screen and (max-width:480px) {
                  #\${values._meta.htmlID} .product-name-cell { height: 19px !important; }
                  #\${values._meta.htmlID} .offer-card-small-badge { font-size: 16px !important; }
                  #\${values._meta.htmlID} .offer-card-small-rule { font-size: 14px !important; }
                  #\${values._meta.htmlID} .product-name { font-size: 18px !important; }
                  #\${values._meta.htmlID} .product-footer-item { font-size: 14px !important; }
                }
                
              \`;
              return cssString;
            },
            js: function(values) {
              const htmlID = values._meta.htmlID;
              const toolbarBestOffer = values.bestOffer || '1';
              const cardModel = values.model || defaultModel;
              
              return \`
                (function() {
                  const htmlID = '\${htmlID}';
                  const toolbarBestOffer = '\${toolbarBestOffer}';
                  const defaultModel = '${emailDefaultModel}';
                  const initialModel = '\${cardModel}';
                  const modelQuantityMap = ${modelQuantityMapJson};
                  
                  const modelNameMap = {
                    'repurchase': 'Recompra',
                    'recovery': 'Resgate',
                    'activation': 'Ativação'
                  };
                  
                  const isTemplateLoad = (() => {
                    try {
                      return typeof window.__offerCardTemplateLoadUntil === 'number' && Date.now() < window.__offerCardTemplateLoadUntil;
                    } catch (e) {
                      return false;
                    }
                  })();
                  
                  if (typeof window.__lastSelectedModel === 'undefined') {
                    window.__lastSelectedModel = defaultModel;
                  } else {
                  }

                  (function setupBestOfferTooltipOnce() {
                    if (window.__offerHelpTipInitialized) return;
                    window.__offerHelpTipInitialized = true;

                    var HELP_TEXT = 'Apenas produtos com imagem e preço/desconto cadastrados são considerados como oferta para esse componente do e-mail.';
                    var ALT_LABELS = ['Melhor Oferta', 'Best Offer'];
                    var TIP_ID = 'offer-best-offer-help';

                    function __txt(n) {
                      return (n && (n.textContent || n.innerText) || '').replace(new RegExp('\\s+', 'g'), ' ').trim();
                    }

                    function createTip() {
                      var tip = document.createElement('span');
                      tip.id = TIP_ID;
                      tip.textContent = 'i';
                      tip.title = HELP_TEXT;
                      tip.setAttribute('aria-label', HELP_TEXT);
                      tip.style.cursor = 'pointer';
                      tip.style.borderRadius = '50%';
                      tip.style.width = '16px';
                      tip.style.height = '16px';
                      tip.style.display = 'inline-flex';
                      tip.style.alignItems = 'center';
                      tip.style.justifyContent = 'center';
                      tip.style.fontSize = '12px';
                      tip.style.lineHeight = '16px';
                      tip.style.border = '1px solid #ccc';
                      tip.style.userSelect = 'none';
                      tip.style.fontFamily = 'sans-serif';
                      tip.style.background = '#fff';
                      tip.style.zIndex = 9999;
                      return tip;
                    }

                    function findAnchor() {
                    var LABEL_SEL = '.blockbuilder-widget-label.mb-2 ' +
                                    '> label.blockbuilder-label-primary ' +
                                    '> .sc-eEVuZf.has-value';

                    function norm(t){ return (t||'').replace(new RegExp('\\s+', 'g'),' ').trim(); }
                    

                    var nodes = document.querySelectorAll(LABEL_SEL);
                    for (var i = 0; i < nodes.length; i++) {
                      var txt = norm(nodes[i].textContent || nodes[i].innerText);
                      if (txt === 'Melhor Oferta' || txt.indexOf('Melhor Oferta') !== -1) {
                        return nodes[i];
                      }
                    }

                    var generic = document.querySelector('.property-panel .label, .unlayer-property-panel .label');
                    return generic || null;
                  }

                    function ensureTipOnce() {
                      var tip = document.getElementById(TIP_ID) || createTip();
                      var anchor = findAnchor();
                      if (anchor && tip.parentElement !== anchor) {
                        try { anchor.appendChild(tip); } catch (e) {
                          try { anchor.insertAdjacentElement('beforeend', tip); } catch(_) {}
                        }
                      }
                    }

                    ensureTipOnce();

                    try {
                      var mo = new MutationObserver(function() {
                        ensureTipOnce();
                      });
                      mo.observe(document.body, { childList: true, subtree: true });
                      window.addEventListener('beforeunload', function(){ try{ mo.disconnect(); }catch(_){ } });
                    } catch(_) {
                      setInterval(ensureTipOnce, 2000);
                    }
                  })();

                  
                  if (!window.offerCardsProcessed) {
                    window.offerCardsProcessed = new Set();
                  }
                  
                  if (window.offerCardsProcessed.has(htmlID)) {
                    return;
                  }
                  
                  window.offerCardsProcessed.add(htmlID);
                  
                  const currentCard = document.getElementById(htmlID);
                  
                  // Declarar isEdit uma única vez no escopo principal
                  const isEdit = ${isEdit};
                  const __savedBestOfferOrder = ${JSON.stringify(bestOfferOrder)};
                  const __isNewCardInEdit = !!(isEdit && (!__savedBestOfferOrder || !__savedBestOfferOrder.find(o => o && o.htmlID === htmlID)));

                  // Debug (habilite com ?offerCardDebug=1 ou localStorage.__offerCardDebug=1 ou window.__offerCardDebug=true)
                  const __offerDebugEnabled = (() => {
                    try {
                      if (typeof window === 'undefined') return false;
                      if (window.__offerCardDebug === true) return true;
                      if (window.location && typeof window.location.search === 'string' && window.location.search.includes('offerCardDebug=1')) return true;
                      if (window.localStorage && window.localStorage.getItem('__offerCardDebug') === '1') return true;
                    } catch (_) {}
                    return false;
                  })();
                  const __dbg = (...args) => {
                    try {
                      if (!__offerDebugEnabled) return;
                      if (typeof console !== 'undefined' && console.log) console.log('[offer_card][auto][bestOffer]', ...args);
                      try {
                        // logar também no console do parent (evita o problema de logs “sumirem” no iframe)
                        if (window.parent && window.parent !== window && window.parent.console && window.parent.console.log) {
                          window.parent.console.log('[offer_card][auto][bestOffer]', ...args);
                        }
                      } catch (_) {}
                    } catch (_) {}
                  };

                  const __fireClick = (el) => {
                    try {
                      if (!el) return false;
                      // alguns componentes do painel só abrem com mousedown antes do click
                      try { el.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true })); } catch (_) {}
                      try { el.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true })); } catch (_) {}
                      if (typeof el.click === 'function') el.click();
                      return true;
                    } catch (_) {
                      return false;
                    }
                  };

                  const __findBestOfferCombobox = () => {
                    // 1) seletores conhecidos
                    let btn =
                      document.querySelector('.bestOffer_panel button[role="combobox"], .bestOffer_panel .dropdown-button, [data-property="bestOffer"] button[role="combobox"], [data-property="bestOffer"] .dropdown-button') ||
                      null;

                    if (btn) return btn;

                    // 2) fallback: qualquer combobox cujo texto/aria-label mencione "Melhor Oferta"
                    const candidates = Array.from(document.querySelectorAll('button[role="combobox"], .dropdown-button, .blockbuilder-dropdown-button'));
                    btn = candidates.find((b) => {
                      const t = ((b.getAttribute && b.getAttribute('aria-label')) || b.textContent || b.innerText || '').replace(/\\s+/g, ' ').trim();
                      return t.includes('Melhor Oferta');
                    }) || null;
                    return btn;
                  };

                  const __openBestOfferDropdown = () => {
                    const btn = __findBestOfferCombobox();
                    __dbg('openDropdown', { htmlID, found: !!btn, aria: btn && btn.getAttribute && btn.getAttribute('aria-label'), cls: btn && btn.className });
                    if (!btn) return false;
                    return __fireClick(btn);
                  };

                  const __pickBestOfferOption = (offerNumber) => {
                    const n = parseInt(offerNumber, 10);
                    const targetPrefix = (isNaN(n) ? '1' : String(n)) + 'ª';
                    const items = Array.from(document.querySelectorAll('.dropdown-item, [role="option"], .blockbuilder-dropdown-item'));
                    __dbg('pickOption', { htmlID, offerNumber: offerNumber, items: items.length });
                    const target = items.find((item) => {
                      const label = item.querySelector('.label, .blockbuilder-label');
                      const txt = (label ? label.textContent : (item.textContent || item.innerText || '')).replace(/\\s+/g, ' ').trim();
                      return txt.startsWith(targetPrefix) && txt.includes('Melhor Oferta');
                    });
                    if (!target) {
                      __dbg('pickOption:not-found', { htmlID, targetPrefix });
                      return false;
                    }
                    __fireClick(target);
                    __dbg('pickOption:clicked', { htmlID, targetPrefix });
                    return true;
                  };

                  const __syncBestOfferDropdown = (offerNumber) => {
                    // tentar abrir e selecionar com retries curtos (painel pode demorar pra renderizar)
                    let tries = 0;
                    const run = () => {
                      tries++;
                      __openBestOfferDropdown();
                      setTimeout(() => {
                        if (__pickBestOfferOption(offerNumber)) return;
                        if (tries < 6) setTimeout(run, 120);
                      }, 80);
                    };
                    run();
                  };
                  
                  const getCardModel = () => {
                    const model = window.__lastSelectedModel || defaultModel;
                    return model;
                  };
                  
                  // Função para contar cards existentes e determinar próximo número POR MODELO
                  const getNextOfferNumber = (model = null, targetHtmlID = null) => {
                    const targetModel = model || getCardModel();
                    const cardIDToUse = targetHtmlID || htmlID;
                    
                    // Se estiver em modo de edição e tiver ordem definida, usar a ordem salva
                    // isEdit já foi declarado no escopo principal acima
                    const bestOfferOrder = __savedBestOfferOrder;
                   
                    if (isEdit && bestOfferOrder && bestOfferOrder.length > 0) {
                      // Primeiro, tentar encontrar pelo htmlID e modelo
                      let currentCardOrder = bestOfferOrder.find(order => order.htmlID === cardIDToUse && order.model === targetModel);
                      
                      // Se não encontrou, tentar apenas pelo htmlID (pode ser que o modelo ainda não esteja definido)
                      if (!currentCardOrder) {
                        currentCardOrder = bestOfferOrder.find(order => order.htmlID === cardIDToUse);
                      }
                      
                      if (currentCardOrder) {
                        const savedNumber = parseInt(currentCardOrder.bestOffer, 10);
                        return savedNumber;
                      }
                    }
                    
                    const unlayerBody = document.body;
                    const allCardsByAria = unlayerBody.querySelectorAll('[aria-offer]');
                    
                    // Também buscar cards pelo elemento customizado (para pegar cards recém-adicionados)
                    const allCardsByClass = unlayerBody.querySelectorAll('.u_content_custom_offer_card');
                    
                    // EXCLUIR o card atual da contagem
                    const currentCardElement = document.getElementById(cardIDToUse);
                    
                    // Capturar número atual (se existir) sem "travar" no default (1) quando já houver outra 1 para o mesmo modelo.
                    // Ao adicionar um novo card, ele geralmente nasce como best-offer-<modelo>-1 e precisa ser renumerado.
                    let currentNumberCandidate = null;
                    const normalizedTargetModel = normalizeModelForAria(targetModel);
                    if (currentCardElement) {
                      const currentAriaOffer = currentCardElement.getAttribute('aria-offer');
                      if (currentAriaOffer) {
                        const modelPattern = 'best-offer-' + normalizedTargetModel + '-';
                        if (currentAriaOffer.startsWith(modelPattern)) {
                          const n = parseInt(currentAriaOffer.replace(modelPattern, ''), 10);
                          if (!isNaN(n)) {
                            currentNumberCandidate = n;
                          }
                        }
                      }
                    }
                    
                    const modelName = modelNameMap[targetModel] || 'Ativação';
                    const existingNumbers = [];
                    
                    // Função auxiliar para extrair número do texto "Xª Melhor Oferta – Modelo"
                    const extractOfferNumberFromText = (text) => {
                      if (!text) return null;
                      const regex = new RegExp('(\\\\d+)ª\\\\s*Melhor\\\\s*Oferta');
                      const match = text.match(regex);
                      if (match && match[1]) {
                        return parseInt(match[1], 10);
                      }
                      return null;
                    };
                    
                    // Processar cards com aria-offer
                    Array.from(allCardsByAria).forEach(card => {
                      const ariaOffer = card.getAttribute('aria-offer');
                      if (!ariaOffer) return;
                      
                      const isCurrentCard = currentCardElement && (card === currentCardElement || currentCardElement.contains(card));
                      if (isCurrentCard) return;
                      
                      const modelPattern = 'best-offer-' + normalizedTargetModel + '-';
                      if (ariaOffer.startsWith(modelPattern)) {
                        const number = parseInt(ariaOffer.replace(modelPattern, ''));
                        if (!isNaN(number)) {
                          existingNumbers.push(number);
                        }
                      }
                    });
                    
                    // Processar cards pelo elemento customizado (verificar texto visível)
                    Array.from(allCardsByClass).forEach(card => {
                      const isCurrentCard = currentCardElement && (card === currentCardElement || currentCardElement.contains(card) || card.id === cardIDToUse);
                      if (isCurrentCard) return;
                      
                      // Primeiro verificar aria-offer (mais confiável)
                      const ariaOffer = card.getAttribute('aria-offer');
                      if (ariaOffer) {
                        const modelPattern = 'best-offer-' + normalizedTargetModel + '-';
                        if (ariaOffer.startsWith(modelPattern)) {
                          const number = parseInt(ariaOffer.replace(modelPattern, ''));
                          if (!isNaN(number) && !existingNumbers.includes(number)) {
                            existingNumbers.push(number);
                            return; // Já encontrou pelo aria-offer, não precisa verificar texto
                          }
                        } else {
                          // Se o aria-offer existe mas não corresponde ao modelo, pular este card
                          return;
                        }
                      }
                      
                      // Se não tem aria-offer, verificar texto visível
                      const nomeOfertaElement = card.querySelector('.product-name, .offer-card-product-name');
                      if (nomeOfertaElement) {
                        const text = nomeOfertaElement.textContent || nomeOfertaElement.innerText || '';
                        // Verificar se o texto termina com " – " + nome do modelo
                        // Isso garante que estamos contando apenas cards do modelo correto
                        const expectedSuffix = ' – ' + modelName;
                        if (text.endsWith(expectedSuffix)) {
                          const number = extractOfferNumberFromText(text);
                          if (number && !isNaN(number) && !existingNumbers.includes(number)) {
                            existingNumbers.push(number);
                          }
                        }
                      }
                    });

                    // Se o card já tiver um número definido (diferente do default 1),
                    // ou se ainda não existe outra "1ª Melhor Oferta" para este modelo, manter o número atual.
                    if (currentNumberCandidate !== null) {
                      if (currentNumberCandidate !== 1 || !existingNumbers.includes(1)) {
                        return currentNumberCandidate;
                      }
                    }
                    
                    // Usar limite máximo baseado na quantidade disponível para este modelo específico
                    const maxOffersForModel = modelQuantityMap[targetModel] || ${Math.min(maxOfferCards, 10)};
                    const maxOffers = Math.min(maxOffersForModel, 10); // Limitar apenas por 10, não por maxOfferCards
                    
                    // Garantir que maxOffers seja pelo menos 1
                    const safeMaxOffers = Math.max(1, maxOffers);
                    
                    // Ordenar números existentes para facilitar debug
                    existingNumbers.sort((a, b) => a - b);
                    
                    // Encontrar próximo número disponível para este modelo
                    let nextNumber = 1;
                    while (existingNumbers.includes(nextNumber) && nextNumber <= safeMaxOffers) {
                      nextNumber++;
                    }
                    
                    // Se atingiu o limite máximo, reutilizar a última oferta disponível
                    if (nextNumber > safeMaxOffers) {
                      nextNumber = safeMaxOffers;
                    }
                    
                    if (nextNumber < 1 || isNaN(nextNumber)) {
                      nextNumber = 1;
                    }
                    
                    return nextNumber;
                  };
                  
                  const normalizeModelForAria = (model) => {
                    if (!model) return model;
                    if (model === 'repurchase' || model === 'recovery' || model === 'activation') {
                      return model;
                    }
                    let normalized = model.replace(/^category_/, '');
                    if (normalized === 'rescue') {
                      normalized = 'recovery';
                    }
                    if (normalized === 'repurchase' || normalized === 'activation') {
                      return normalized;
                    }
                    return normalized;
                  };
                  
                  const denormalizeModelFromAria = (normalizedModel) => {
                    if (!normalizedModel) return normalizedModel;
                    return normalizedModel;
                  };
                  
                  const buildAriaOffer = (model, order) => {
                    const normalizedModel = normalizeModelForAria(model);
                    return 'best-offer-' + normalizedModel + '-' + order;
                  };
                  
                  const selectCardAndOpenDropdown = () => {
                    setTimeout(() => {
                      const cardElement = document.getElementById(htmlID);
                      if (cardElement) {
                        cardElement.click();
                        
                        setTimeout(() => {
                          const model = getCardModel();
                          
                          if (window.updateBestOfferDropdownForModel) {
                            window.updateBestOfferDropdownForModel(model, 0);
                            setTimeout(() => {
                              window.updateBestOfferDropdownForModel(model, 0);
                            }, 200);
                            setTimeout(() => {
                              window.updateBestOfferDropdownForModel(model, 0);
                            }, 500);
                            setTimeout(() => {
                              window.updateBestOfferDropdownForModel(model, 0);
                            }, 800);
                          }
                          
                          const offerNumber = getNextOfferNumber(model);
                          const modelName = modelNameMap[model] || 'Ativação';
                          const targetOfferText = offerNumber + 'ª Melhor Oferta – ' + modelName;
                          const targetOfferTextSimple = offerNumber + 'ª Melhor Oferta';
                          
                          const allOptions = Array.from(document.querySelectorAll('.dropdown-item'));
                          const targetOfferOption = allOptions.find(item => {
                            const label = item.querySelector('.label');
                            const labelText = label ? label.textContent.trim() : '';
                            return labelText === targetOfferText || labelText === targetOfferTextSimple || labelText.startsWith(offerNumber + 'ª');
                          });
                          
                          if (targetOfferOption) {
                            targetOfferOption.click();
                            __dbg('selected via DOM dropdown-item', { htmlID, model, offerNumber });
                          } else {
                            __dbg('dropdown-item not found (will sync)', { htmlID, model, offerNumber });
                            __syncBestOfferDropdown(offerNumber);
                          }
                        }, 500);
                      }
                    }, 500);
                  };
                  
                  const tryImmediateExecution = () => {
                    const cardElement = document.getElementById(htmlID);
                    if (cardElement) {
                      cardElement.click();
                      
                      const model = getCardModel();
                      
                      if (window.updateBestOfferDropdownForModel) {
                        window.updateBestOfferDropdownForModel(model, 0);
                        setTimeout(() => {
                          window.updateBestOfferDropdownForModel(model, 0);
                        }, 200);
                        setTimeout(() => {
                          window.updateBestOfferDropdownForModel(model, 0);
                        }, 500);
                        setTimeout(() => {
                          window.updateBestOfferDropdownForModel(model, 0);
                        }, 800);
                      }
                      
                      const offerNumber = getNextOfferNumber(model);
                      const modelName = modelNameMap[model] || 'Ativação';
                      const targetOfferText = offerNumber + 'ª Melhor Oferta – ' + modelName;
                      const targetOfferTextSimple = offerNumber + 'ª Melhor Oferta';
                      
                      const targetOfferOption = Array.from(document.querySelectorAll('.dropdown-item')).find(item => {
                        const label = item.querySelector('.label');
                        const labelText = label ? label.textContent.trim() : '';
                        return labelText === targetOfferText || labelText === targetOfferTextSimple || labelText.startsWith(offerNumber + 'ª');
                      });
                      
                      if (targetOfferOption) {
                        targetOfferOption.click();
                        __dbg('immediate selected via DOM dropdown-item', { htmlID, model, offerNumber });
                        return true;
                      } else {
                        __dbg('immediate dropdown-item not found (will sync)', { htmlID, model, offerNumber });
                        __syncBestOfferDropdown(offerNumber);
                      }
                    }
                    return false;
                  };
                  
                  // Em modo edição, também executar quando o card é NOVO (não existe no bestOfferOrder salvo)
                  if ((!isEdit || __isNewCardInEdit) && !isTemplateLoad) {
                    if (!tryImmediateExecution()) {
                      selectCardAndOpenDropdown();
                    }
                  }
                  
                  const tryUnlayerAPI = () => {
                    setTimeout(() => {
                      if (window.unlayer && window.unlayer.getEditor) {
                        try {
                          const editor = window.unlayer.getEditor();
                          if (editor) {
                            const element = editor.getSelectedElement();
                            if (element && element.id === htmlID) {
                              setTimeout(() => {
                                const model = getCardModel();
                                
                                if (window.updateBestOfferDropdownForModel) {
                                  window.updateBestOfferDropdownForModel(model, 0);
                                }
                                
                                const offerNumber = getNextOfferNumber(model);
                                const modelName = modelNameMap[model] || 'Ativação';
                                const targetOfferText = offerNumber + 'ª Melhor Oferta – ' + modelName;
                                const targetOfferTextSimple = offerNumber + 'ª Melhor Oferta';
                                
                                const targetOfferOption = Array.from(document.querySelectorAll('.dropdown-item')).find(item => {
                                  const label = item.querySelector('.label');
                                  const labelText = label ? label.textContent.trim() : '';
                                  return labelText === targetOfferText || labelText === targetOfferTextSimple || labelText.startsWith(offerNumber + 'ª');
                                });
                                
                                if (targetOfferOption) {
                                  targetOfferOption.click();
                                  __dbg('unlayerAPI selected via DOM dropdown-item', { htmlID, model, offerNumber });
                                } else {
                                  __dbg('unlayerAPI dropdown-item not found (will sync)', { htmlID, model, offerNumber });
                                  __syncBestOfferDropdown(offerNumber);
                                }
                              }, 200);
                            }
                          }
                        } catch (error) {
                        }
                      }
                    }, 800);
                  };
                  
                  const isEditForAPI = ${isEdit};
                  if ((!isEditForAPI || __isNewCardInEdit) && !isTemplateLoad) {
                    tryUnlayerAPI();
                  }
                  
                  const setupUnlayerEventListeners = () => {
                    if (window.unlayer) {
                      try {
                        window.unlayer.addEventListener('design:updated', () => {
                          setTimeout(() => {
                            const selectedElement = document.querySelector('.unlayer-element-selected, .selected');
                            if (selectedElement && selectedElement.id === htmlID) {
                              setTimeout(() => {
                                const model = getCardModel();
                                const offerNumber = getNextOfferNumber(model);
                                const modelName = modelNameMap[model] || 'Ativação';
                                const targetOfferText = offerNumber + 'ª Melhor Oferta – ' + modelName;
                                const targetOfferTextSimple = offerNumber + 'ª Melhor Oferta';
                                
                                const targetOfferOption = Array.from(document.querySelectorAll('.dropdown-item')).find(item => {
                                  const label = item.querySelector('.label');
                                  const labelText = label ? label.textContent.trim() : '';
                                  return labelText === targetOfferText || labelText === targetOfferTextSimple || labelText.startsWith(offerNumber + 'ª');
                                });
                                if (targetOfferOption) {
                                  targetOfferOption.click();
                                }
                              }, 500);
                            }
                          }, 100);
                        });
                      } catch (error) {
                      }
                    }
                  };
                  
                  if (!isTemplateLoad) {
                    setupUnlayerEventListeners();
                  }
                  
                  const setupMutationObserver = () => {
                    const isEditForObserver = ${isEdit};
                    if (isEditForObserver || isTemplateLoad) {
                      return;
                    }
                    
                    const observer = new MutationObserver((mutations) => {
                      mutations.forEach((mutation) => {
                        if (mutation.type === 'childList') {
                          const addedNodes = Array.from(mutation.addedNodes);
                          const hasPropertyPanel = addedNodes.some(node => {
                            if (node.nodeType === Node.ELEMENT_NODE) {
                              return node.classList?.contains('property-panel') || 
                                     node.classList?.contains('unlayer-property-panel') ||
                                     node.querySelector?.('.property-panel, .unlayer-property-panel');
                            }
                            return false;
                          });
                          
                          if (hasPropertyPanel) {
                            const model = getCardModel();
                            
                            if (window.updateBestOfferDropdownForModel) {
                              setTimeout(() => {
                                window.updateBestOfferDropdownForModel(model, 0);
                              }, 100);
                            }
                            
                            const offerNumber = getNextOfferNumber(model);
                            const modelName = modelNameMap[model] || 'Ativação';
                            const targetOfferText = offerNumber + 'ª Melhor Oferta – ' + modelName;
                            const targetOfferTextSimple = offerNumber + 'ª Melhor Oferta';
                            
                            const targetOfferOption = Array.from(document.querySelectorAll('.dropdown-item')).find(item => {
                              const label = item.querySelector('.label');
                              const labelText = label ? label.textContent.trim() : '';
                              return labelText === targetOfferText || labelText === targetOfferTextSimple || labelText.startsWith(offerNumber + 'ª');
                            });
                            
                            if (targetOfferOption) {
                              targetOfferOption.click();
                              observer.disconnect();
                            }
                          }
                        }
                      });
                    });
                    
                    observer.observe(document.body, {
                      childList: true,
                      subtree: true
                    });
                    
                    setTimeout(() => {
                      observer.disconnect();
                    }, 5000);
                  };
                  
                  setupMutationObserver();
                  
                  const updateBestOfferDropdownForCurrentModel = (retryCount = 0) => {
                    const currentModel = getCardModel();
                    const maxQuantityForModel = modelQuantityMap[currentModel] || ${Math.min(maxOfferCards, 10)};
                    const maxOffers = maxQuantityForModel > 0 ? Math.min(maxQuantityForModel, 10) : ${Math.min(maxOfferCards, 10)};
                    
                    let dropdownItems = document.querySelectorAll('.dropdown-item, [role="option"], .blockbuilder-dropdown-item');
                    
                    if (dropdownItems.length > 0) {
                      const bestOfferItems = Array.from(dropdownItems).filter(item => {
                        const label = item.querySelector('.label, .blockbuilder-label');
                        if (!label) return false;
                        const text = label.textContent || label.innerText || '';
                        return text.includes('Melhor Oferta') || text.includes('ª');
                      });
                      
                      if (bestOfferItems.length > 0) {
                        dropdownItems = bestOfferItems;
                      }
                    }
                    
                    if (dropdownItems.length === 0) {
                      
                      let bestOfferPanel = document.querySelector('.bestOffer_panel, [data-property="bestOffer"]') ||
                                           document.querySelector('[data-property="bestOffer"]') ||
                                           document.querySelector('.property-panel [data-property="bestOffer"]') ||
                                           document.querySelector('.unlayer-property-panel [data-property="bestOffer"]');
                      
                      if (!bestOfferPanel) {
                        
                        if (retryCount < 5) {
                          setTimeout(() => {
                            updateBestOfferDropdownForCurrentModel(retryCount + 1);
                          }, 200 * (retryCount + 1));
                        }
                        return;
                      }
                      
                      const dropdownMenu = bestOfferPanel.querySelector('.dropdown-menu, [role="listbox"], .unlayer-dropdown-menu, .blockbuilder-dropdown-menu');
                      if (!dropdownMenu) {
                        return;
                      }
                      
                      dropdownItems = dropdownMenu.querySelectorAll('.dropdown-item, [role="option"], .blockbuilder-dropdown-item');
                    }
                    
                    if (dropdownItems.length === 0) {
                      return;
                    }
                    
                    let hiddenCount = 0;
                    let visibleCount = 0;
                    
                    const itemsArray = Array.isArray(dropdownItems) ? dropdownItems : Array.from(dropdownItems);
                    
                    itemsArray.forEach((item, index) => {
                      const label = item.querySelector('.label, .blockbuilder-label');
                      if (!label) {
                        return;
                      }
                      
                      const itemText = label.textContent || label.innerText || '';
                      
                      let numberMatch = itemText.match(/(\\\\d+)/);
                      
                      if (!numberMatch) {
                        numberMatch = itemText.match(/^(\\\\d+)/);
                      }
                      if (!numberMatch) {
                        const numbers = itemText.match(/\\\\d+/g);
                        if (numbers && numbers.length > 0) {
                          numberMatch = [numbers[0], numbers[0]];
                        }
                      }
                      if (!numberMatch) {
                        const firstDigit = itemText.match(/[0-9]/);
                        if (firstDigit) {
                          numberMatch = [firstDigit[0], firstDigit[0]];
                        } else {
                          return;
                        }
                      }
                      
                      const itemNumber = parseInt(numberMatch[1] || numberMatch[0], 10);
                      
                      if (itemNumber > maxOffers) {
                        item.style.display = 'none';
                        item.style.visibility = 'hidden';
                        item.setAttribute('aria-hidden', 'true');
                        hiddenCount++;
                      } else {
                        item.style.display = '';
                        item.style.visibility = '';
                        item.removeAttribute('aria-hidden');
                        visibleCount++;
                      }
                    });
                    
                  };
                  
                  // Expor função globalmente para poder ser chamada de outros lugares
                  // Usar a função do escopo superior se disponível, senão usar a local
                  if (window.updateBestOfferDropdownForModel) {
                    // Já existe, não sobrescrever
                  } else {
                    window.updateBestOfferDropdownForModel = updateBestOfferDropdownForCurrentModel;
                  }
                  
                  const setupBlockBuilderObserver = () => {
                    const blockBuilderObserver = new MutationObserver((mutations) => {
                      mutations.forEach((mutation) => {
                        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                          const target = mutation.target;
                          
                          if (target.classList && 
                              target.classList.contains('blockbuilder-layer') && 
                              target.classList.contains('blockbuilder-layer-selectable') &&
                              target.classList.contains('blockbuilder-layer-selected')) {
                            
                            const cardElement = target.id === htmlID ? target : target.querySelector('#' + htmlID);
                            if (!cardElement) return;
                            
                            // Atualizar dropdown quando o card é selecionado
                            setTimeout(() => {
                              updateBestOfferDropdownForCurrentModel();
                            }, 100);
                            
                            let melhorOferta = 'Não identificado';
                            const nomeOfertaElement = cardElement.querySelector('.product-name, .offer-card-product-name');
                            if (nomeOfertaElement && nomeOfertaElement.textContent) {
                              melhorOferta = nomeOfertaElement.textContent.trim();
                            }
                            
                            let modeloText = '';
                            if (melhorOferta && melhorOferta.includes('–')) {
                              const parts = melhorOferta.split('–');
                              if (parts.length > 1) {
                                modeloText = parts[1].trim();
                              }
                            }
                            
                            if (modeloText) {
                              const modelPanel = document.querySelector('.model_panel, [data-property="model"]');
                              if (modelPanel) {
                                const modelButton = modelPanel.querySelector('button[role="combobox"], .dropdown-button');
                                if (modelButton) {
                                  const modelLabel = modelButton.querySelector('.label');
                                  if (modelLabel && modelLabel.textContent.trim() !== modeloText) {
                                    modelLabel.textContent = modeloText;
                                    modelButton.setAttribute('aria-label', modeloText);
                                    modelButton.dispatchEvent(new Event('change', { bubbles: true }));
                                  }
                                }
                              }
                            }
                            
                            const melhorOfertaPanel = document.querySelector('.bestOffer_panel, [data-property="bestOffer"]');
                            if (melhorOfertaPanel) {
                              const dropdownButton = melhorOfertaPanel.querySelector('button[role="combobox"], .dropdown-button');
                              if (dropdownButton) {
                                const labelSpan = dropdownButton.querySelector('.label');
                                if (labelSpan && labelSpan.textContent.trim() !== melhorOferta) {
                                  labelSpan.textContent = melhorOferta;
                                }
                                
                                dropdownButton.setAttribute('aria-label', melhorOferta);
                                dropdownButton.dispatchEvent(new Event('change', { bubbles: true }));
                              }
                            }
                          }
                        }
                        
                        // Detectar quando o dropdown é aberto
                        if (mutation.type === 'childList') {
                          const addedNodes = Array.from(mutation.addedNodes);
                          const hasDropdownMenu = addedNodes.some(node => {
                            if (node.nodeType === Node.ELEMENT_NODE) {
                              return node.classList?.contains('dropdown-menu') || 
                                     node.classList?.contains('unlayer-dropdown-menu') ||
                                     node.querySelector?.('.dropdown-menu, .unlayer-dropdown-menu');
                            }
                            return false;
                          });
                          
                          if (hasDropdownMenu) {
                            const currentModel = getCardModel();
                            
                            // Atualizar dropdown baseado no modelo atual quando o dropdown é aberto
                            // Usar a função do escopo superior se disponível (mais eficiente)
                            if (window.updateBestOfferDropdownForModel) {
                              // Chamar imediatamente e também após um pequeno delay para garantir
                              window.updateBestOfferDropdownForModel(currentModel, 0);
                              setTimeout(() => {
                                window.updateBestOfferDropdownForModel(currentModel, 0);
                              }, 100);
                            } else {
                              setTimeout(() => {
                                updateBestOfferDropdownForCurrentModel();
                              }, 50);
                            }
                          }
                        }
                      });
                    });

                    const observerConfig = {
                      subtree: true,
                      attributes: true,
                      attributeFilter: ['class'],
                    };

                    blockBuilderObserver.observe(document.body, observerConfig);
                  };

                  if (!isTemplateLoad) {
                    setupBlockBuilderObserver();
                  }
                  
                  const setupAriaLabelSync = () => {
                    const syncAriaLabelWithSpan = (button) => {
                      const ariaLabel = button.getAttribute('aria-label');
                      const spanLabel = button.querySelector('.label');
                      
                      if (ariaLabel && spanLabel && ariaLabel !== spanLabel.textContent) {
                        spanLabel.textContent = ariaLabel;
                      }
                    };
                    
                    const syncAllDropdownButtons = () => {
                      const dropdownButtons = document.querySelectorAll('button[role="combobox"].dropdown-button');
                      dropdownButtons.forEach(button => {
                        syncAriaLabelWithSpan(button);
                      });
                    };
                    
                    syncAllDropdownButtons();
                    
                    const ariaLabelObserver = new MutationObserver((mutations) => {
                      mutations.forEach((mutation) => {
                        if (mutation.type === 'attributes' && mutation.attributeName === 'aria-label') {
                          const button = mutation.target;
                          if (button && button.classList.contains('dropdown-button')) {
                            syncAriaLabelWithSpan(button);
                          }
                        }
                      });
                    });
                    
                    const newButtonsObserver = new MutationObserver((mutations) => {
                      mutations.forEach((mutation) => {
                        if (mutation.type === 'childList') {
                          mutation.addedNodes.forEach((node) => {
                            if (node.nodeType === Node.ELEMENT_NODE) {
                              if (node.classList && node.classList.contains('dropdown-button')) {
                                syncAriaLabelWithSpan(node);
                                ariaLabelObserver.observe(node, {
                                  attributes: true,
                                  attributeFilter: ['aria-label']
                                });
                              }
                              
                              const dropdownButtons = node.querySelectorAll && node.querySelectorAll('button[role="combobox"].dropdown-button');
                              if (dropdownButtons) {
                                dropdownButtons.forEach(button => {
                                  syncAriaLabelWithSpan(button);
                                  ariaLabelObserver.observe(button, {
                                    attributes: true,
                                    attributeFilter: ['aria-label']
                                  });
                                });
                              }
                            }
                          });
                        }
                      });
                    });
                    
                    newButtonsObserver.observe(document.body, {
                      childList: true,
                      subtree: true
                    });
                    
                    const existingButtons = document.querySelectorAll('button[role="combobox"].dropdown-button');
                    existingButtons.forEach(button => {
                      ariaLabelObserver.observe(button, {
                        attributes: true,
                        attributeFilter: ['aria-label']
                      });
                    });
                    
                    setInterval(syncAllDropdownButtons, 1000);
                  };
                  
                  setupAriaLabelSync();
                  
                  let model = initialModel || defaultModel;
                  
                  // Primeiro, tentar obter do design salvo de forma síncrona através do bestOfferOrder
                  const bestOfferOrder = ${JSON.stringify(bestOfferOrder)};
                  if (isEdit && bestOfferOrder && bestOfferOrder.length > 0) {
                    const savedOrder = bestOfferOrder.find(order => order.htmlID === htmlID);
                    if (savedOrder) {
                      if (savedOrder.model) {
                        model = savedOrder.model;
                        if (typeof window !== 'undefined') {
                          window.__lastSelectedModel = model;
                          if (!window.cardModelMap) {
                            window.cardModelMap = new Map();
                          }
                          if (htmlID) {
                            window.cardModelMap.set(htmlID, model);
                          }
                        }
                      }
                    }
                  }
                  
                  // Se não encontrou no bestOfferOrder, tentar obter do design de forma assíncrona
                  if (model === defaultModel && isEdit && window.unlayer && window.unlayer.getEditor) {
                    try {
                      const editor = window.unlayer.getEditor();
                      if (editor && editor.saveDesign) {
                        editor.saveDesign((design) => {
                          if (design?.body?.rows) {
                            for (const row of design.body.rows) {
                              if (row.columns) {
                                for (const column of row.columns) {
                                  if (column.contents) {
                                    for (const content of column.contents) {
                                      if (content.type === 'custom' && content.slug === 'offer_card') {
                                        if (content.values?._meta?.htmlID === htmlID) {
                                          if (content.values?.model && content.values.model !== defaultModel) {
                                            const savedModel = content.values.model;
                                            if (typeof window !== 'undefined') {
                                              window.__lastSelectedModel = savedModel;
                                            }
                                            if (currentCard) {
                                              const savedOfferNumber = content.values?.bestOffer || '1';
                                              const savedModelName = modelNameMap[savedModel] || 'Ativação';
                                              const savedOfferName = savedOfferNumber + 'ª Melhor Oferta – ' + savedModelName;
                                              
                                              const nomeOfertaElement = currentCard.querySelector('.product-name, .offer-card-product-name');
                                              if (nomeOfertaElement) {
                                                nomeOfertaElement.textContent = savedOfferName;
                                              }
                                              
                                              if (editor.updateSelectedElement) {
                                                editor.updateSelectedElement({
                                                  model: savedModel,
                                                  bestOffer: savedOfferNumber,
                                                  'nome da oferta': savedOfferName
                                                });
                                              }
                                            }
                                            return;
                                          }
                                        }
                                      }
                                    }
                                  }
                                }
                              }
                            }
                          }
                        });
                      }
                    } catch (e) {
                    }
                  }
                  
                  if (!isTemplateLoad && model === defaultModel) {
                    model = window.__lastSelectedModel || getCardModel();
                  }
                  
                  let offerNumber = isTemplateLoad ? parseInt(toolbarBestOffer, 10) : null;
                  if (isTemplateLoad && (!offerNumber || isNaN(offerNumber) || offerNumber < 1)) {
                    offerNumber = 1;
                  }

                  if (!isTemplateLoad && isEdit && bestOfferOrder && bestOfferOrder.length > 0) {
                    const savedOrder = bestOfferOrder.find(order => order.htmlID === htmlID && order.model === model);
                    if (savedOrder && savedOrder.bestOffer) {
                      offerNumber = parseInt(savedOrder.bestOffer, 10);
                    }
                  }
                  
                  if (!offerNumber || isNaN(offerNumber) || offerNumber < 1) {
                    offerNumber = getNextOfferNumber(model);
                  }
                  
                  if (!offerNumber || offerNumber < 1 || isNaN(offerNumber)) {
                    offerNumber = 1;
                  }
                  
                  const modelName = modelNameMap[model] || 'Ativação';
                  const offerName = offerNumber + 'ª Melhor Oferta – ' + modelName;
                  
                  if (currentCard) {
                    const ariaOfferValue = buildAriaOffer(model, offerNumber);
                    currentCard.setAttribute('aria-offer', ariaOfferValue);
                    
                    const nomeOfertaElement = currentCard.querySelector('.product-name, .offer-card-product-name');
                    if (nomeOfertaElement) {
                      nomeOfertaElement.textContent = offerName;
                    }
                    
                    // No modo edição, quando o card é recém-adicionado ele não existe na ordem salva (bestOfferOrder).
                    // Nesses casos, precisamos setar bestOffer/model/"nome da oferta" diretamente no design também,
                    // sem depender de abrir painel/dropdown (que é instável).
                    const isNewCardInEdit = !!(isEdit && (!bestOfferOrder || !bestOfferOrder.find(order => order && order.htmlID === htmlID)));
                    if (!isEdit || isNewCardInEdit) {
                      const updateCardValues = () => {
                        if (!window.unlayer || !window.unlayer.getEditor) {
                          return;
                        }
                        
                        try {
                          const editor = window.unlayer.getEditor();
                          if (!editor) {
                            return;
                          }
                          
                          if (editor && editor.saveDesign) {
                            editor.saveDesign((design) => {
                              if (!design?.body) {
                                return;
                              }
                              
                              const findAndUpdateCard = (obj) => {
                                if (obj.body?.rows) {
                                  for (const row of obj.body.rows) {
                                    if (row.columns) {
                                      for (const column of row.columns) {
                                        if (column.contents) {
                                          for (const content of column.contents) {
                                            if (content.type === 'custom' && content.slug === 'offer_card') {
                                              if (content.values?._meta?.htmlID === htmlID) {
                                                if (!content.values) content.values = {};
                                                
                                                const needsUpdate = 
                                                  (!content.values.bestOffer || content.values.bestOffer === '1') ||
                                                  (!content.values.model || content.values.model === defaultModel) ||
                                                  (content.values.bestOffer !== offerNumber.toString());
                                                
                                                if (needsUpdate) {
                                                  const oldModel = content.values.model;
                                                  if (window.__lastSelectedModel && window.__lastSelectedModel !== defaultModel) {
                                                    content.values.model = window.__lastSelectedModel;
                                                  } else if (!content.values.model || content.values.model === defaultModel) {
                                                    content.values.model = model;
                                                  } else {
                                                  }
                                                  
                                                  content.values.bestOffer = offerNumber.toString();
                                                  content.values['nome da oferta'] = offerName;
                                                  
                                                  editor.loadDesign(design);
                                                  
                                                  setTimeout(() => {
                                                    updateDropdownsFromVisualText(offerName, modelName);
                                                    
                                                    const bestOfferPanel = document.querySelector('.bestOffer_panel, [data-property="bestOffer"]');
                                                    if (bestOfferPanel) {
                                                      const dropdownButton = bestOfferPanel.querySelector('button[role="combobox"], .dropdown-button');
                                                      if (dropdownButton) {
                                                        dropdownButton.click();
                                                        setTimeout(() => {
                                                          const allOptions = Array.from(document.querySelectorAll('.dropdown-item'));
                                                          const offerNumberSimple = offerNumber + 'ª Melhor Oferta';
                                                          const targetOption = allOptions.find(item => {
                                                            const label = item.querySelector('.label');
                                                            const labelText = label ? label.textContent.trim() : '';
                                                            return labelText === offerName || labelText === offerNumberSimple || labelText.startsWith(offerNumber + 'ª');
                                                          });
                                                          
                                                          if (targetOption) {
                                                            targetOption.click();
                                                          }
                                                        }, 200);
                                                      }
                                                    }
                                                  }, 300);
                                                  
                                                  return true;
                                                }
                                              }
                                            }
                                          }
                                        }
                                      }
                                    }
                                  }
                                }
                                return false;
                              };
                              
                              findAndUpdateCard(design);
                            });
                          } else if (editor && editor.updateSelectedElement) {
                            const updateValues = {
                              bestOffer: offerNumber.toString(),
                              'nome da oferta': offerName
                            };
                            
                            if (window.__lastSelectedModel && window.__lastSelectedModel !== defaultModel) {
                              updateValues.model = window.__lastSelectedModel;
                            } else {
                              updateValues.model = model;
                            }
                            
                            
                            editor.updateSelectedElement(updateValues);
                            
                            setTimeout(() => {
                              updateDropdownsFromVisualText(offerName, modelName);
                              
                              const bestOfferPanel = document.querySelector('.bestOffer_panel, [data-property="bestOffer"]');
                              if (bestOfferPanel) {
                                const dropdownButton = bestOfferPanel.querySelector('button[role="combobox"], .dropdown-button');
                                if (dropdownButton) {
                                  dropdownButton.click();
                                  setTimeout(() => {
                                    const offerNumberSimple = offerNumber + 'ª Melhor Oferta';
                                    const targetOption = Array.from(document.querySelectorAll('.dropdown-item')).find(item => {
                                      const label = item.querySelector('.label');
                                      const labelText = label ? label.textContent.trim() : '';
                                      return labelText === offerName || labelText === offerNumberSimple || labelText.startsWith(offerNumber + 'ª');
                                    });
                                    if (targetOption) {
                                      targetOption.click();
                                    }
                                  }, 200);
                                }
                              }
                            }, 300);
                          }
                        } catch (e) {
                        }
                      };
                      
                      updateCardValues();
                      setTimeout(updateCardValues, 100);
                      setTimeout(updateCardValues, 300);
                    }
                  }
                  const reorderAllOfferCards = () => {
                    const unlayerBody = document.body;
                    const allCards = unlayerBody.querySelectorAll('[aria-offer]');
                    
                    const cardsByModel = {};
                    Array.from(allCards).forEach(card => {
                      const ariaOffer = card.getAttribute('aria-offer');
                      if (ariaOffer && ariaOffer.startsWith('best-offer-')) {
                        const parts = ariaOffer.split('-');
                        if (parts.length >= 4) {
                          const model = parts.slice(2, -1).join('-');
                          if (model && model.length > 0) {
                            if (!cardsByModel[model]) {
                              cardsByModel[model] = [];
                            }
                            cardsByModel[model].push(card);
                          }
                        }
                      }
                    });
                    
                    Object.keys(cardsByModel).forEach(normalizedModel => {
                      const modelCards = cardsByModel[normalizedModel];
                      const modelName = modelNameMap[normalizedModel] || 'Ativação';
                      
                      const maxOffersForModel = modelQuantityMap[normalizedModel] || ${Math.min(maxOfferCards, 10)};
                      const maxOffers = Math.min(maxOffersForModel, ${maxOfferCards});
                      
                      const sortedCards = modelCards.sort((a, b) => {
                        const aRect = a.getBoundingClientRect();
                        const bRect = b.getBoundingClientRect();
                        return aRect.top - bRect.top || aRect.left - bRect.left;
                      });
                      
                      sortedCards.forEach((card, index) => {
                        const newNumber = Math.min(index + 1, maxOffers);
                        const ariaOfferValue = buildAriaOffer(normalizedModel, newNumber);
                        card.setAttribute('aria-offer', ariaOfferValue);
                        
                        const nomeOfertaElement = card.querySelector('.product-name, .offer-card-product-name');
                        if (nomeOfertaElement) {
                          nomeOfertaElement.textContent = newNumber + 'ª Melhor Oferta – ' + modelName;
                        }
                      });
                    });

                  };

                  window.reorderAllOfferCards = reorderAllOfferCards;
                  
                  if (window.addEventListener) {
                    window.addEventListener('message', (event) => {
                      if (event.data && event.data.type === 'configureOfferCard' && event.data.source === 'parent-window') {
                        const { cardId, offerNumber, model } = event.data;
                        if (cardId && window.configureOfferCard) {
                          window.configureOfferCard(cardId, offerNumber, model);
                        }
                      }
                    });
                  }
                  
                  window.configureOfferCard = (cardId, offerNumber = null, model = null) => {
                    const cardElement = document.getElementById(cardId);
                    if (!cardElement) {
                      return;
                    }
                    
                    let targetModel = model;
                    if (!targetModel) {
                      const isEdit = ${isEdit};
                      const bestOfferOrder = ${JSON.stringify(bestOfferOrder)};
                      
                      
                      if (isEdit && bestOfferOrder && bestOfferOrder.length > 0) {
                        const savedOrder = bestOfferOrder.find(order => order.htmlID === cardId);
                        if (savedOrder && savedOrder.model) {
                          targetModel = savedOrder.model;
                        }
                      }
                      
                      if (!targetModel) {
                        const ariaOffer = cardElement.getAttribute('aria-offer');
                        if (ariaOffer && ariaOffer.startsWith('best-offer-')) {
                          const parts = ariaOffer.split('-');
                          if (parts.length >= 4) {
                            const modelFromAria = parts.slice(2, -1).join('-');
                            if (modelFromAria && (modelFromAria === 'repurchase' || modelFromAria === 'recovery' || modelFromAria === 'activation')) {
                              targetModel = modelFromAria;
                            }
                          }
                        }
                      }
                      
                      if (!targetModel) {
                        targetModel = getCardModel();
                      }
                    }

                    const modelName = modelNameMap[targetModel] || 'Ativação';
                    const normalizedTargetModel = normalizeModelForAria(targetModel);
                    
                    if (!offerNumber) {
                      const bestOfferOrder = ${JSON.stringify(bestOfferOrder)};
                      
                      if (isEdit && bestOfferOrder && bestOfferOrder.length > 0) {
                        const savedOrder = bestOfferOrder.find(order => order.htmlID === cardId && order.model === targetModel);
                        if (savedOrder) {
                          offerNumber = savedOrder.bestOffer;
                        }
                      }
                      
                      if (!offerNumber) {
                        const ariaOffer = cardElement.getAttribute('aria-offer');
                        if (ariaOffer) {
                          const modelPattern = 'best-offer-' + normalizedTargetModel + '-';
                          if (ariaOffer.startsWith(modelPattern)) {
                            const extractedNumber = ariaOffer.replace(modelPattern, '');
                            if (extractedNumber && !isNaN(parseInt(extractedNumber))) {
                              offerNumber = extractedNumber;
                            }
                          }
                        }
                      }
                      
                      if (!offerNumber) {
                        offerNumber = getNextOfferNumber(targetModel, cardId);
                      }
                    }
                    
                    offerNumber = offerNumber ? offerNumber.toString() : '1';
                    
                    const targetOfferText = offerNumber + 'ª Melhor Oferta – ' + modelName;
                    
                    const ariaOfferValue = buildAriaOffer(targetModel, offerNumber);
                    cardElement.setAttribute('aria-offer', ariaOfferValue);
                    
                    const nomeOfertaElement = cardElement.querySelector('.product-name, .offer-card-product-name');
                    if (nomeOfertaElement) {
                      const textoAntes = nomeOfertaElement.textContent;
                      nomeOfertaElement.textContent = targetOfferText;
                      const textoDepois = nomeOfertaElement.textContent;
                    } else {
                    }
                    
                    cardElement.click();
                    
                    setTimeout(() => {
                      if (window.unlayer && window.unlayer.getEditor) {
                        try {
                          const editor = window.unlayer.getEditor();
                          if (editor && editor.updateSelectedElement) {
                            const updateValues = {
                              bestOffer: offerNumber.toString(),
                              model: targetModel,
                              'nome da oferta': targetOfferText
                            };
                            editor.updateSelectedElement(updateValues);
                            
                            setTimeout(() => {
                              editor.saveDesign((design) => {
                                if (design?.body?.rows) {
                                  for (const row of design.body.rows) {
                                    if (row.columns) {
                                      for (const column of row.columns) {
                                        if (column.contents) {
                                          for (const content of column.contents) {
                                            if (content.type === 'custom' && content.slug === 'offer_card') {
                                              if (content.values?._meta?.htmlID === cardId) {
                                              }
                                            }
                                          }
                                        }
                                      }
                                    }
                                  }
                                }
                              });
                            }, 100);
                          }
                        } catch (e) {
                        }
                      }
                      
                      const nomeOfertaElementAfter = cardElement.querySelector('.product-name, .offer-card-product-name');
                      const targetOption = Array.from(document.querySelectorAll('.dropdown-item')).find(item => {
                        const label = item.querySelector('.label');
                        const labelText = label ? label.textContent.trim() : '';
                        return labelText === targetOfferText || 
                               labelText === (offerNumber + 'ª Melhor Oferta') ||
                               labelText.startsWith(offerNumber + 'ª');
                      });
                      
                      if (targetOption) {
                        targetOption.click();
                        __dbg('configureOfferCard selected via DOM dropdown-item', { cardId, targetModel, offerNumber });
                      } else {
                        __dbg('configureOfferCard dropdown-item not found (will sync)', { cardId, targetModel, offerNumber });
                        __syncBestOfferDropdown(offerNumber);
                      }
                    }, 200);
                  };
                  
                  window.clearOfferCardProcessed = (cardId) => {
                    if (window.offerCardsProcessed) {
                      window.offerCardsProcessed.delete(cardId);
                    }
                  };
                  
                  window.setMaxOfferCards = (maxCards) => {
                    window.maxOfferCards = maxCards;
                  };
                  
                  
                  function checkAriaOffer() {
                    const container = document.getElementById(htmlID);
                    if (container) {
                      const model = getCardModel();
                      const badges = container.querySelectorAll('.offer-card-small-container, .offer-card-container');
                      badges.forEach((badge, index) => {
                        const ariaOffer = badge.getAttribute('aria-offer');
                        if (!ariaOffer || ariaOffer.includes('{{')) {
                          const defaultAriaOffer = buildAriaOffer(model, 1);
                          badge.setAttribute('aria-offer', defaultAriaOffer);
                        }
                      });
                    }
                  }
                  
                  setTimeout(() => {
                    const container = document.getElementById(htmlID);
                    if (container) {
                      const badges = container.querySelectorAll('.offer-card-small-container, .offer-card-container');
                      const needsUpdate = Array.from(badges).some(badge => {
                        const ariaOffer = badge.getAttribute('aria-offer');
                        return !ariaOffer || ariaOffer.includes('{{');
                      });
                      
                      if (needsUpdate) {
                        checkAriaOffer();
                      }
                    }
                  }, 100);
                  
                  const updateDropdownsFromVisualText = (offerName, modelName) => {
                    const modelPanel = document.querySelector('.model_panel, [data-property="model"]');
                    if (modelPanel) {
                      const modelButton = modelPanel.querySelector('button[role="combobox"], .dropdown-button');
                      if (modelButton) {
                        const modelLabel = modelButton.querySelector('.label');
                        if (modelLabel && modelLabel.textContent.trim() !== modelName) {
                          modelLabel.textContent = modelName;
                          modelButton.setAttribute('aria-label', modelName);
                          modelButton.dispatchEvent(new Event('change', { bubbles: true }));
                        }
                      }
                    }
                    
                    // Atualizar dropdown de bestOffer
                    const bestOfferPanel = document.querySelector('.bestOffer_panel, [data-property="bestOffer"]');
                    if (bestOfferPanel) {
                      const bestOfferButton = bestOfferPanel.querySelector('button[role="combobox"], .dropdown-button');
                      if (bestOfferButton) {
                        const labelSpan = bestOfferButton.querySelector('.label');
                        const currentText = labelSpan ? labelSpan.textContent.trim() : '';
                        
                        // Extrair o número da oferta do offerName (ex: "2ª Melhor Oferta – Recompra" -> "2")
                        const offerNumberMatch = offerName.match(/(\\d+)/);
                        const offerNumber = offerNumberMatch ? offerNumberMatch[1] : null;
                        
                        if (labelSpan && currentText !== offerName) {
                          labelSpan.textContent = offerName;
                          bestOfferButton.setAttribute('aria-label', offerName);
                          
                          // Tentar atualizar o valor real no Unlayer também
                          if (window.unlayer && window.unlayer.getEditor && offerNumber) {
                            try {
                              const editor = window.unlayer.getEditor();
                              if (editor && editor.updateSelectedElement) {
                                editor.updateSelectedElement({
                                  bestOffer: offerNumber
                                });
                              }
                            } catch (e) {
                              // Ignorar erros
                            }
                          }
                          
                          bestOfferButton.dispatchEvent(new Event('change', { bubbles: true }));
                        }
                      }
                    }
                  };
                  
                  if (typeof window !== 'undefined') {
                    window.addEventListener('offerCardModelChange', (event) => {
                      window.__lastSelectedModel = event.detail.model;
                      if (event.detail.htmlID === htmlID) {
                        const newModel = event.detail.model;
                        const newOfferNumber = getNextOfferNumber(newModel);
                        const newModelName = modelNameMap[newModel] || 'Ativação';
                        const newOfferName = newOfferNumber + 'ª Melhor Oferta – ' + newModelName;
                        
                        // Atualizar dropdown quando o modelo mudar
                        const maxQuantityForModel = modelQuantityMap[newModel] || ${Math.min(maxOfferCards, 10)};
                        // Garantir que maxOffers use o valor correto - se maxQuantityForModel > 0, usar ele (limitado por 10)
                        const maxOffers = maxQuantityForModel > 0 ? Math.min(maxQuantityForModel, 10) : ${Math.min(maxOfferCards, 10)};
                        
                        const updateDropdownForNewModel = (retryCount = 0) => {
                          // Tentar vários seletores
                          let bestOfferPanel = document.querySelector('.bestOffer_panel, [data-property="bestOffer"]') ||
                                               document.querySelector('[data-property="bestOffer"]') ||
                                               document.querySelector('.property-panel [data-property="bestOffer"]') ||
                                               document.querySelector('.unlayer-property-panel [data-property="bestOffer"]');
                          
                          if (!bestOfferPanel) {
                            
                            // Retry até 5 vezes com delay crescente
                            if (retryCount < 5) {
                              setTimeout(() => {
                                updateDropdownForNewModel(retryCount + 1);
                              }, 200 * (retryCount + 1));
                            }
                            return;
                          }
                          
                          const dropdownMenu = bestOfferPanel.querySelector('.dropdown-menu, [role="listbox"], .unlayer-dropdown-menu, .blockbuilder-dropdown-menu');
                          if (!dropdownMenu) {
                            return;
                          }
                          
                          const dropdownItems = dropdownMenu.querySelectorAll('.dropdown-item, [role="option"], .blockbuilder-dropdown-item');
                          
                          let hiddenCount = 0;
                          let visibleCount = 0;
                          
                          dropdownItems.forEach((item) => {
                            const label = item.querySelector('.label, .blockbuilder-label');
                            if (!label) return;
                            
                            const itemText = label.textContent || label.innerText || '';
                            const numberMatch = itemText.match(/(\\d+)/);
                            if (!numberMatch) return;
                            
                            const itemNumber = parseInt(numberMatch[1], 10);
                            
                            if (itemNumber > maxOffers) {
                              item.style.display = 'none';
                              item.style.visibility = 'hidden';
                              item.setAttribute('aria-hidden', 'true');
                              hiddenCount++;
                            } else {
                              item.style.display = '';
                              item.style.visibility = '';
                              item.removeAttribute('aria-hidden');
                              visibleCount++;
                            }
                          });
                          
                        };
                        
                        // Atualizar imediatamente e também após um delay para garantir que o dropdown foi renderizado
                        updateDropdownForNewModel();
                        setTimeout(() => {
                          updateDropdownForNewModel();
                        }, 100);
                        setTimeout(() => {
                          updateDropdownForNewModel();
                        }, 300);
                        
                        if (currentCard) {
                          const newAriaOffer = buildAriaOffer(newModel, newOfferNumber);
                          currentCard.setAttribute('aria-offer', newAriaOffer);
                          
                          const badges = currentCard.querySelectorAll('.offer-card-small-container, .offer-card-container');
                          badges.forEach(badge => {
                            badge.setAttribute('aria-offer', newAriaOffer);
                          });
                          
                          const nomeOfertaElement = currentCard.querySelector('.product-name, .offer-card-product-name');
                          if (nomeOfertaElement) {
                            const oldText = nomeOfertaElement.textContent;
                            nomeOfertaElement.textContent = newOfferName;
                          }
                        }
                        
                        setTimeout(() => {
                          if (window.unlayer && window.unlayer.getEditor) {
                            try {
                              const editor = window.unlayer.getEditor();
                              if (editor && editor.saveDesign) {
                                editor.saveDesign((design) => {
                                  if (!design?.body) return;
                                  
                                  const findAndUpdateCard = (obj) => {
                                    if (obj.body?.rows) {
                                      for (const row of obj.body.rows) {
                                        if (row.columns) {
                                          for (const column of row.columns) {
                                            if (column.contents) {
                                              for (const content of column.contents) {
                                                if (content.type === 'custom' && content.slug === 'offer_card') {
                                                  if (content.values?._meta?.htmlID === htmlID) {
                                                    if (!content.values) content.values = {};
                                                    content.values.bestOffer = newOfferNumber.toString();
                                                    content.values['nome da oferta'] = newOfferName;
                                                    content.values.model = newModel; // newModel já está definido no escopo acima
                                                    return true;
                                                  }
                                                }
                                              }
                                            }
                                          }
                                        }
                                      }
                                    }
                                    return false;
                                  };
                                  
                                  if (findAndUpdateCard(design)) {
                                    // Recarregar o design para aplicar as mudanças
                                    editor.loadDesign(design);
                                    
                                    // Aguardar um pouco e atualizar os dropdowns visualmente
                                    setTimeout(() => {
                                      updateDropdownsFromVisualText(newOfferName, newModelName);
                                    }, 300);
                                  }
                                });
                              } else if (editor && editor.updateSelectedElement) {
                                editor.updateSelectedElement({
                                  bestOffer: newOfferNumber.toString(),
                                  'nome da oferta': newOfferName,
                                  model: newModel
                                });
                                
                                setTimeout(() => {
                                  const bestOfferPanel = document.querySelector('.bestOffer_panel, [data-property="bestOffer"]');
                                  if (bestOfferPanel) {
                                    const bestOfferButton = bestOfferPanel.querySelector('button[role="combobox"], .dropdown-button');
                                    if (bestOfferButton) {
                                      const labelSpan = bestOfferButton.querySelector('.label');
                                      if (labelSpan) {
                                        labelSpan.textContent = newOfferName;
                                      }
                                      bestOfferButton.setAttribute('aria-label', newOfferName);
                                      bestOfferButton.dispatchEvent(new Event('change', { bubbles: true }));
                                    }
                                  }
                                }, 200);
                              }
                            } catch (e) {
                              // Ignorar erros
                            }
                          }
                        }, 150);
                      }
                    });
                  }
                  
                  // Listener para mudanças no bestOffer - atualizar dropdowns
                  if (typeof window !== 'undefined') {
                    window.addEventListener('offerCardBestOfferChange', (event) => {
                      // Se o evento foi disparado para este card, atualizar dropdowns
                      if (event.detail.htmlID === htmlID) {
                        setTimeout(() => {
                          updateDropdownsFromVisualText(event.detail.offerName, event.detail.modelName);
                        }, 100);
                      }
                    });
                  }
                })();
              \`;
            }
          }
        }
      });
      
      function hideDuplicateButtons() {
        const allDuplicateButtons = document.querySelectorAll('.blockbuilder-duplicate');
        
        allDuplicateButtons.forEach(button => {
          const blockbuilderLayer = button.closest('.blockbuilder-layer');
          if (blockbuilderLayer && blockbuilderLayer.querySelector('.u_content_custom_offer_card')) {
            button.style.display = 'none';
            button.style.visibility = 'hidden';
            button.style.opacity = '0';
            button.style.pointerEvents = 'none';
          }
        });
      }
      
      hideDuplicateButtons();
      setTimeout(hideDuplicateButtons, 100);
      setTimeout(hideDuplicateButtons, 500);
      setTimeout(hideDuplicateButtons, 1000);
      
      const observer = new MutationObserver(function(mutations) {
        let hasChanges = false;
        
        mutations.forEach(function(mutation) {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach(function(node) {
              if (node.nodeType === 1) {
                if (node.classList && node.classList.contains('blockbuilder-duplicate')) {
                  hasChanges = true;
                } else if (node.classList && node.classList.contains('u_content_custom_offer_card')) {
                  hasChanges = true;
                } else if (node.classList && node.classList.contains('blockbuilder-layer')) {
                  hasChanges = true;
                } else if (node.querySelector && node.querySelector('.u_content_custom_offer_card')) {
                  hasChanges = true;
                } else if (node.querySelector && node.querySelector('.blockbuilder-duplicate')) {
                  hasChanges = true;
                }
              }
            });
          }
        });
        
        if (hasChanges) {
          hideDuplicateButtons();
          setTimeout(hideDuplicateButtons, 50);
          setTimeout(hideDuplicateButtons, 200);
        }
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
      
      setInterval(hideDuplicateButtons, 2000);
    })();
  `
}
